/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS — HubSpot Review. A live CRM hygiene & enrichment audit over the
   WHOLE CRM (every company, every open deal, all sources and pipelines).
   Mirrors the original app's page: clickable tiles that open the companies /
   deals behind them, filterable owner and conglomerate tables (conglomerates
   expand to their member companies), the duplicate-deal groups, and the Sales
   POC-wise gap table whose counts open that owner's affected deals — every
   row with a HubSpot deep-link.
   ──────────────────────────────────────────────────────────────────────────── */
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Paper, Typography } from '@mui/material'
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts'
import ApartmentIcon from '@mui/icons-material/Apartment'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import GroupsIcon from '@mui/icons-material/Groups'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useGetHubspotReviewQuery, useGetHubspotCompanyGapQuery } from '../../redux/growthosHubspotReviewApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_CARD_SX, GX_COLORS, GX_INK_500, GX_INK_700, GX_INK_900, GX_PAGE_SX, GX_POSITIVE, GxSyncStatusBar,
} from '../growthos-shared/GrowthOsUi'
import {
  Badge, Clean, CountButton, DataTable, DrillBox, GX_INK_400, HsLink, Panel, Tile,
  currency, hsCompanySearchUrl, hsRecordUrl, integer, shortDate,
} from './HsKit'

const MSME_COLORS = { Micro: '#06B6D4', Small: '#0EA5E9', Medium: '#6366F1', Large: '#7C3AED', Unknown: '#94A3B8', 'Not set': '#E11D48' }
const HUBSPOT_ONLY = { hubspot: 'HubSpot', hubspot_api: 'HubSpot' }

const companyUrl = (hub, id) => hsRecordUrl(hub, '0-2', id)
const dealUrl = (hub, id) => hsRecordUrl(hub, '0-3', id)
const idle = (d) => (d.daysSinceActivity == null ? 'Never' : integer(d.daysSinceActivity))

/** The companies behind one enrichment-gap tile — fetched when the tile is opened. */
function CompanyGapTable({ gap, hub }) {
  const { data, isFetching, error } = useGetHubspotCompanyGapQuery(gap)
  if (isFetching && !data) return <Clean text="Loading companies…" />
  if (error) return <Clean text={`Failed to load — ${error.message || `HTTP ${error.status}`}`} />
  const rows = data?.rows || []
  if (!rows.length) return <Clean />
  return (
    <Box sx={{ p: 1.5 }}>
      <DataTable
        rows={rows}
        rowKey={(c) => c.id}
        filter={(c, q) => `${c.name ?? ''} ${c.owner ?? ''} ${c.team ?? ''} ${c.msme ?? ''} ${c.industry ?? ''} ${c.city ?? ''}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter by company, owner, industry…"
        columns={[
          { key: 'company', header: 'Company', title: 'Company name.', sortValue: (c) => c.name ?? '', cell: (c) => <b>{c.name || '—'}</b> },
          { key: 'owner', header: 'Owner', title: 'Raw HubSpot owner — may not be a classified Sales POC.', sortValue: (c) => c.owner ?? '', cell: (c) => c.owner ?? '—' },
          { key: 'team', header: 'Team', title: "The owner's team tag (Data → Sales POCs).", sortValue: (c) => c.team ?? '', cell: (c) => c.team ?? '—' },
          { key: 'msme', header: 'MSME', title: 'MSME classification from HubSpot (Micro/Small/Medium/Large).', sortValue: (c) => c.msme ?? '', cell: (c) => c.msme ?? '—' },
          { key: 'industry', header: 'Industry', title: 'Company industry (HubSpot).', sortValue: (c) => c.industry ?? '', cell: (c) => c.industry ?? '—' },
          { key: 'city', header: 'City', title: 'Company city (HubSpot).', sortValue: (c) => c.city ?? '', cell: (c) => c.city ?? '—' },
          { key: 'country', header: 'Country', title: 'Company country (HubSpot).', sortValue: (c) => c.country ?? '', cell: (c) => c.country ?? '—' },
          { key: 'hs', header: 'HubSpot', title: 'Opens the record in HubSpot.', cell: (c) => <HsLink href={companyUrl(hub, c.id)} /> },
        ]}
      />
    </Box>
  )
}

function DealTable({ rows, hub }) {
  if (!rows || !rows.length) return <Clean />
  return (
    <Box sx={{ p: 1.5 }}>
      <DataTable
        rows={rows}
        rowKey={(d, i) => `${d.id}-${i}`}
        filter={(d, q) => `${d.name ?? ''} ${d.owner} ${d.pipeline ?? ''} ${d.stage ?? ''} ${d.temperature ?? ''} ${d.dealSource ?? ''}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter by deal, owner, pipeline…"
        columns={[
          { key: 'deal', header: 'Deal', title: 'HubSpot deal name.', sortValue: (d) => d.name ?? '', cell: (d) => <b>{d.name || '—'}</b> },
          { key: 'owner', header: 'Owner', title: 'Raw HubSpot owner — may not be a classified Sales POC.', sortValue: (d) => d.owner, cell: (d) => d.owner },
          { key: 'temp', header: 'Temp', title: 'Deal temperature (Hot / Warm / Cold / Hibernating).', sortValue: (d) => d.temperature ?? '', cell: (d) => d.temperature ?? '—' },
          { key: 'pipeline', header: 'Pipeline', title: 'HubSpot pipeline.', sortValue: (d) => d.pipeline ?? '', cell: (d) => d.pipeline ?? '—' },
          { key: 'stage', header: 'Stage', title: 'Current HubSpot pipeline stage.', sortValue: (d) => d.stage ?? '', cell: (d) => d.stage ?? '—' },
          { key: 'amount', header: 'Amount', title: 'HubSpot deal Amount (₹).', align: 'right', sortValue: (d) => d.amount, cell: (d) => currency(d.amount) },
          { key: 'mbr', header: 'Est MBR', title: 'Estimated Monthly Billing Revenue (₹/month) on the deal.', align: 'right', sortValue: (d) => d.estimatedMbr, cell: (d) => currency(d.estimatedMbr) },
          { key: 'source', header: 'Source', title: 'Deal source.', sortValue: (d) => d.dealSource ?? '', cell: (d) => d.dealSource ?? '—' },
          { key: 'close', header: 'Expected Close', title: "The deal's expected close date.", sortValue: (d) => d.expectedCloseDate ?? '', cell: (d) => shortDate(d.expectedCloseDate) },
          { key: 'lastAct', header: 'Last Activity', title: "The deal's last activity date (HubSpot).", sortValue: (d) => d.lastActivityAt ?? '', cell: (d) => shortDate(d.lastActivityAt) },
          { key: 'idle', header: 'Days Idle', title: "Days since the deal's last activity, measured at the last HubSpot sync.", align: 'right', sortValue: (d) => d.daysSinceActivity ?? -1, cell: idle },
          { key: 'hs', header: 'HubSpot', title: 'Opens the record in HubSpot.', cell: (d) => <HsLink href={dealUrl(hub, d.id)} /> },
        ]}
      />
    </Box>
  )
}

/** Suspected duplicate open deals, grouped by company + pipeline. Each row is a separate
 *  HubSpot record (distinct Deal ID) — merge or close them in HubSpot; nothing is auto-merged. */
function DuplicateGroups({ groups, hub }) {
  const rows = (groups || []).flatMap((g) => g.deals.map((deal) => ({ company: g.companyName, pipeline: g.pipeline, groupSize: g.deals.length, deal })))
  if (!rows.length) return <Clean text="No duplicate open deals — clean on this check." />
  return (
    <Box sx={{ p: 1.5 }}>
      <Typography sx={{ mb: 1, fontSize: 12, color: GX_INK_500 }}>
        Same company + same pipeline + the same deal name once you ignore casing, spacing and the product suffix
        (WH / Warehousing / D2C / Freight…). Each line is a <b>separate HubSpot record</b> with its own Deal ID, so all of
        them are counted in every open-deal metric until they are merged in HubSpot. Genuinely different per-location
        deals (e.g. “V-Guard_Davangere” vs “V-Guard_Hyderabad”) are not flagged.
      </Typography>
      <DataTable
        rows={rows}
        rowKey={(r, i) => `${r.deal.id}-${i}`}
        filter={(r, q) => `${r.company} ${r.deal.name ?? ''} ${r.deal.owner}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter company, deal, owner…"
        columns={[
          { key: 'company', header: 'Company', title: 'The HubSpot company all these deals hang off.', sortValue: (r) => r.company, cell: (r) => <b>{r.company}</b> },
          { key: 'n', header: 'Dupes', title: 'How many open deals are in this duplicate group.', align: 'right', sortValue: (r) => r.groupSize, cell: (r) => <Badge color="#be123c" bg="rgba(225,29,72,0.12)">{r.groupSize}</Badge> },
          { key: 'deal', header: 'Deal', title: 'HubSpot deal name.', sortValue: (r) => r.deal.name ?? '', cell: (r) => r.deal.name },
          { key: 'id', header: 'Deal ID', title: 'The HubSpot record ID — proof these are distinct records, not one deal listed twice.', sortValue: (r) => r.deal.id, cell: (r) => <Box component="span" sx={{ fontSize: 11, color: GX_INK_400 }}>{r.deal.id}</Box> },
          { key: 'pipeline', header: 'Pipeline', title: 'HubSpot pipeline.', sortValue: (r) => r.pipeline ?? '', cell: (r) => r.pipeline ?? '—' },
          { key: 'stage', header: 'Stage', title: 'Current stage — duplicates often sit at different stages.', sortValue: (r) => r.deal.stage ?? '', cell: (r) => r.deal.stage ?? '—' },
          { key: 'temp', header: 'Temp', title: 'Deal temperature.', sortValue: (r) => r.deal.temperature ?? '', cell: (r) => r.deal.temperature ?? '—' },
          { key: 'mbr', header: 'Est MBR', title: 'Estimated Monthly Billing Revenue (₹/month).', align: 'right', sortValue: (r) => r.deal.estimatedMbr, cell: (r) => currency(r.deal.estimatedMbr) },
          { key: 'owner', header: 'Owner', title: 'Deal owner.', sortValue: (r) => r.deal.owner, cell: (r) => r.deal.owner },
          { key: 'idle', header: 'Days Idle', title: 'Days since last activity.', align: 'right', sortValue: (r) => r.deal.daysSinceActivity ?? -1, cell: (r) => idle(r.deal) },
          { key: 'hs', header: 'HubSpot', title: 'Open the record in HubSpot to merge or close it.', cell: (r) => <HsLink href={dealUrl(hub, r.deal.id)} /> },
        ]}
      />
    </Box>
  )
}

function OwnerTable({ rows }) {
  return (
    <Box sx={{ p: 1.5 }}>
      <DataTable
        rows={rows || []}
        rowKey={(o, i) => o.personId || `${o.owner}-${i}`}
        filter={(o, q) => `${o.owner} ${o.team ?? ''} ${o.role}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter by user, team or role…"
        columns={[
          { key: 'user', header: 'HubSpot User', sortValue: (o) => o.owner, cell: (o) => <b>{o.owner}</b> },
          { key: 'team', header: 'Team', title: "The Sales POC's team tag (Data → Sales POCs).", sortValue: (o) => o.team ?? '', cell: (o) => o.team ?? '—' },
          { key: 'role', header: 'Role', title: "Sales / Support / Unclassified — from the owner's team tag (Data → Sales POCs).", sortValue: (o) => o.role,
            cell: (o) => (o.role === 'Sales' ? <Badge color={GX_POSITIVE}>Sales</Badge>
              : o.role === 'Support' ? <Badge color="#b45309">Support · reassign</Badge>
                : <Badge color={GX_INK_400}>Unclassified</Badge>) },
          { key: 'companies', header: 'Companies', title: 'Number of companies.', align: 'right', sortValue: (o) => o.companies, cell: (o) => integer(o.companies) },
        ]}
      />
    </Box>
  )
}

function ConglomerateTable({ rows, hub }) {
  const [openName, setOpenName] = useState(null)
  return (
    <Box sx={{ p: 1.5 }}>
      <DataTable
        rows={rows || []}
        rowKey={(r) => r.conglomerate}
        filter={(r, q) => r.conglomerate.toLowerCase().includes(q)}
        filterPlaceholder="Filter conglomerate…"
        emptyText="None mapped."
        onRowClick={(r) => setOpenName((cur) => (cur === r.conglomerate ? null : r.conglomerate))}
        isRowActive={(r) => openName === r.conglomerate}
        renderExpanded={(r) => (
          <Box sx={{ bgcolor: '#f8fafc', p: 1.5 }}>
            <DataTable
              rows={r.members || []}
              rowKey={(m) => m.id}
              columns={[
                { key: 'company', header: 'Company', title: 'Company name.', sortValue: (m) => m.name ?? '', cell: (m) => <b>{m.name || '—'}</b> },
                { key: 'hs', header: 'HubSpot', title: 'Opens the record in HubSpot.', cell: (m) => <HsLink href={companyUrl(hub, m.id)} /> },
              ]}
            />
            {r.associatedCompanies > (r.members || []).length && (
              <Typography sx={{ p: 0.5, fontSize: 12, color: GX_INK_400 }}>Showing first {integer(r.members.length)} of {integer(r.associatedCompanies)}.</Typography>
            )}
          </Box>
        )}
        columns={[
          { key: 'conglomerate', header: 'Conglomerate', sortValue: (r) => r.conglomerate,
            cell: (r) => (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: '#4338ca' }}>
                <ExpandMoreIcon sx={{ fontSize: 15, transition: 'transform .15s', transform: openName === r.conglomerate ? 'rotate(180deg)' : 'none' }} />{r.conglomerate}
              </Box>
            ) },
          { key: 'count', header: 'Associated Companies', align: 'right', sortValue: (r) => r.associatedCompanies, cell: (r) => integer(r.associatedCompanies) },
          { key: 'hs', header: 'HubSpot', title: 'Searches HubSpot companies for this conglomerate name.', cell: (r) => <HsLink href={hsCompanySearchUrl(hub, r.conglomerate)} label="Search" /> },
        ]}
      />
    </Box>
  )
}

// Which deals feed each gap column, and which columns that gap's detail table shows.
// Matching is by the stable owner id (name only when the owner is unresolved), so two
// same-name owners can never see each other's deals.
const ownerMatch = (r) => (x) => (r.personId != null ? x.ownerPersonId === r.personId : x.ownerPersonId == null && x.owner === r.owner)
const GAP_DEFS = {
  hotIdle: { deals: (D, r) => D.hotStale.filter(ownerMatch(r)), cols: 'activity' },
  warmIdle: { deals: (D, r) => D.warmStale.filter(ownerMatch(r)), cols: 'activity' },
  amountNeMbr: { deals: (D, r) => D.amountMbrMismatch.filter(ownerMatch(r)), cols: 'amount' },
  mktStale: { deals: (D, r) => [...D.mktHotStale, ...D.mktWarmStale].filter(ownerMatch(r)), cols: 'mktactivity' },
  expectedClosePastToday: { deals: (D, r) => D.expectedClosePast.filter(ownerMatch(r)), cols: 'close' },
  temperatureBlank: { deals: (D, r) => D.temperatureBlank.filter(ownerMatch(r)), cols: 'temp' },
  coldOrHibernating: { deals: (D, r) => D.coldHibernating.filter(ownerMatch(r)), cols: 'mktactivity' },
}

function GapDetail({ list, cols, hub }) {
  if (!list.length) return <Clean text="No matching deals." />
  const dealCol = { key: 'deal', header: 'Deal', sortValue: (d) => d.name ?? '', cell: (d) => <b>{d.name}</b> }
  const hsCol = { key: 'hs', header: 'HubSpot', cell: (d) => <HsLink href={dealUrl(hub, d.id)} /> }
  const amountCol = { key: 'amount', header: 'Amount', align: 'right', sortValue: (d) => d.amount, cell: (d) => currency(d.amount) }
  const mbrCol = { key: 'mbr', header: 'Est MBR', align: 'right', sortValue: (d) => d.estimatedMbr, cell: (d) => currency(d.estimatedMbr) }
  const pipelineCol = { key: 'pipeline', header: 'Pipeline', sortValue: (d) => d.pipeline ?? '', cell: (d) => d.pipeline ?? '—' }
  const stageCol = { key: 'stage', header: 'Stage', sortValue: (d) => d.stage ?? '', cell: (d) => d.stage ?? '—' }
  const closeCol = { key: 'close', header: 'Expected Close', sortValue: (d) => d.expectedCloseDate ?? '', cell: (d) => shortDate(d.expectedCloseDate) }
  const tempCol = { key: 'temp', header: 'Temp', sortValue: (d) => d.temperature ?? '', cell: (d) => d.temperature ?? '—' }
  const lastActCol = { key: 'lastAct', header: 'Last Activity', sortValue: (d) => d.lastActivityAt ?? '', cell: (d) => shortDate(d.lastActivityAt) }
  const idleCol = { key: 'idle', header: 'Days Idle', align: 'right', sortValue: (d) => d.daysSinceActivity ?? -1, cell: idle }
  const columns = cols === 'amount' ? [dealCol, amountCol, mbrCol, hsCol]
    : cols === 'close' ? [dealCol, pipelineCol, stageCol, closeCol, hsCol]
      : cols === 'temp' ? [dealCol, pipelineCol, stageCol, hsCol]
        : cols === 'mktactivity' ? [dealCol, tempCol, lastActCol, idleCol, hsCol]
          : [dealCol, lastActCol, idleCol, hsCol]
  return <Box sx={{ p: 1.5 }}><DataTable rows={list} rowKey={(d, i) => `${d.id}-${i}`} columns={columns} /></Box>
}

function PocGapTable({ rows, deals, hub }) {
  const [open, setOpen] = useState(null) // { owner, type }
  const gapKey = (r) => (r.personId != null ? `p${r.personId}` : r.owner)
  const gapCell = (r, type) => {
    const k = gapKey(r)
    const active = open?.owner === k && open?.type === type
    return <CountButton value={r[type]} active={active} onClick={() => setOpen(active ? null : { owner: k, type })} />
  }
  const num = (key, header, title) => ({ key, header, title, align: 'right', sortValue: (r) => r[key], cell: (r) => gapCell(r, key) })
  return (
    <Box sx={{ p: 1.5 }}>
      <Typography sx={{ mb: 1, fontSize: 12, color: GX_INK_400 }}>Click any non-zero count to list that owner&apos;s affected deals with a HubSpot link.</Typography>
      <DataTable
        rows={rows || []}
        rowKey={(r) => gapKey(r)}
        filter={(r, q) => `${r.owner} ${r.team ?? ''}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter by POC…"
        emptyText="No gaps."
        isRowActive={(r) => open?.owner === gapKey(r)}
        renderExpanded={(r) => (open ? (
          <Box sx={{ bgcolor: '#f8fafc' }}>
            <GapDetail list={GAP_DEFS[open.type].deals(deals, r)} cols={GAP_DEFS[open.type].cols} hub={hub} />
          </Box>
        ) : null)}
        columns={[
          { key: 'poc', header: 'Sales POC', title: "The account's Sales POC — the open deal's HubSpot owner.", sortValue: (r) => r.owner,
            cell: (r) => (r.personId
              ? <Link to={`/growthos/pocs-analytics/${r.personId}`} style={{ color: GX_COLORS.indigo, fontWeight: 600, textDecoration: 'none' }}>{r.owner}</Link>
              : <b>{r.owner}</b>) },
          { key: 'team', header: 'Team', title: "The Sales POC's team tag (Data → Sales POCs).", sortValue: (r) => r.team ?? '', cell: (r) => r.team ?? '—' },
          num('hotIdle', 'Hot idle', 'Hot deals (open) with no activity for over the idle threshold.'),
          num('warmIdle', 'Warm idle', 'Warm deals (open) with no activity for over the idle threshold.'),
          num('amountNeMbr', 'Amount≠MBR', 'Deal Amount is set and differs from Est. MBR (blank amounts excluded).'),
          num('mktStale', 'Mktg idle', 'Marketing-sourced Hot/Warm deals gone idle — a subset of Hot/Warm idle.'),
          num('expectedClosePastToday', 'Close overdue', 'Hot/Warm deals whose expected close date is past the last-sync date.'),
          num('temperatureBlank', 'Temp blank', 'Open deals with NO temperature set (Cold/Hibernating excluded).'),
          { key: 'total', header: 'Total gaps', title: 'Hot idle + Warm idle + Amount≠MBR + Close overdue + Temp blank. Mktg idle (subset of Hot/Warm idle) and Cold/Hib. (parked book) are not added.', align: 'right', sortValue: (r) => r.total, cell: (r) => <b>{integer(r.total)}</b> },
          { key: 'coldOrHibernating', header: 'Cold/Hib.', title: 'Open deals parked as Cold/Hibernating — a stale-pipeline signal, NOT counted in Total gaps. Hover a count for the ₹/month parked.', align: 'right', sortValue: (r) => r.coldOrHibernating,
            cell: (r) => <span title={r.coldHibernatingMbr > 0 ? `${currency(r.coldHibernatingMbr)}/month Est. MBR parked` : undefined}>{gapCell(r, 'coldOrHibernating')}</span> },
        ]}
      />
    </Box>
  )
}

function SectionHeading({ icon, color, title, right }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: GX_INK_900 }}>{title}</Typography>
      {right}
    </Box>
  )
}

const grid = (cols) => ({ display: 'grid', gap: 1.5, mb: 2, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: `repeat(${cols}, 1fr)` } })

export default function HubspotReviewPage() {
  const { data, isLoading, error } = useGetHubspotReviewQuery()
  const { data: runsData } = useListIngestRunsQuery()
  const [open, setOpen] = useState(null)
  const toggle = (k) => setOpen(open === k ? null : k)
  const drill = (k, node) => (open === k ? <DrillBox>{node}</DrillBox> : null)
  const c = data?.companies
  const h = data?.openDealHygiene
  const D = h?.deals
  const hub = data?.hubspot
  const gc = c?.gapCounts || {}
  const staleDays = h?.idleThresholdDays ?? 7
  const asOf = data?.asOf || h?.asOf

  return (
    <Box sx={GX_PAGE_SX}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <FactCheckIcon sx={{ fontSize: 15, color: GX_COLORS.indigo }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_COLORS.indigo }}>CRM Hygiene &amp; Enrichment Audit</Typography>
        </Box>
        <Typography sx={{ fontSize: 26, fontWeight: 700, color: GX_INK_900 }}>HubSpot Review</Typography>
        <Typography sx={{ fontSize: 14, color: GX_INK_500, mt: 0.5, maxWidth: 900 }}>
          A live audit of company enrichment and open-deal hygiene. Enterprise↔SME uses <b>MSME Classification</b> (Large =
          Enterprise; Micro/Small/Medium = SME). Tiles are clickable; each table has a filter and a per-row HubSpot link
          {asOf ? ` · data as of ${shortDate(asOf)}` : ''}.
        </Typography>
        <Box sx={{ mt: 1.5 }}><GxSyncStatusBar runs={runsData?.runs || []} labels={HUBSPOT_ONLY} /></Box>
      </Box>

      {error && (
        <Box sx={{ border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', borderRadius: '16px', p: 2, mb: 2, fontSize: 13 }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {isLoading && <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 4, textAlign: 'center', fontSize: 13, color: GX_INK_400 }}>Loading…</Paper>}

      {c && h && D && (
        <>
          {/* ── Companies ─────────────────────────────────────────────── */}
          <Box sx={{ mb: 4 }}>
            <SectionHeading icon={<ApartmentIcon sx={{ fontSize: 18 }} />} color="#6366f1" title="Companies" />
            <Box sx={grid(4)}>
              <Tile label="Total Companies" value={integer(c.total)} sub={`${integer(c.owned)} owned`} tone="indigo" />
              <Tile label="SME (Micro/Small/Medium)" value={integer(c.sme)} tone="teal" />
              <Tile label="Enterprise (Large)" value={integer(c.enterprise)} tone="indigo" />
              <Tile label="Unclassified" value={integer(c.unclassified)} sub="No MSME class" tone="rose" active={open === 'msme'} onClick={() => toggle('msme')} />
            </Box>
            {drill('msme', <CompanyGapTable gap="msme" hub={hub} />)}

            <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2.5, mb: 2 }}>
              <Typography sx={{ mb: 1.5, fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>
                MSME Classification <Box component="span" sx={{ fontWeight: 400, color: GX_INK_400 }}>· SME = Micro/Small/Medium · Enterprise = Large</Box>
              </Typography>
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(c.msmeBreakdown || []).filter((e) => e.count > 0)} margin={{ top: 16, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: '#334155' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <RTooltip formatter={(v) => [integer(v), 'Companies']} cursor={{ fill: 'rgba(99,102,241,.06)' }} />
                    <Bar dataKey="count" name="Companies" radius={[6, 6, 0, 0]} maxBarSize={90} isAnimationActive={false}>
                      <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: '#475569' }} formatter={(v) => integer(v)} />
                      {(c.msmeBreakdown || []).filter((e) => e.count > 0).map((e) => <Cell key={e.bucket} fill={MSME_COLORS[e.bucket] || '#6366f1'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Enrichment gaps */}
            <Box sx={grid(6)}>
              <Tile label="Unowned" value={integer(gc.unowned)} tone="rose" active={open === 'unowned'} onClick={() => toggle('unowned')} />
              <Tile label="Support-team Owner" value={integer(gc.nonSalesOwner)} sub="Reassign to a POC" tone="rose" active={open === 'nonSalesOwner'} onClick={() => toggle('nonSalesOwner')} />
              <Tile label="Owner Unclassified" value={integer(gc.ownerUnclassified)} sub="Tag team in Data → POCs" tone="amber" active={open === 'ownerUnclassified'} onClick={() => toggle('ownerUnclassified')} />
              <Tile label="No Industry" value={integer(gc.industry)} tone="amber" active={open === 'industry'} onClick={() => toggle('industry')} />
              <Tile label="No City/Country" value={integer(gc.location)} tone="amber" active={open === 'location'} onClick={() => toggle('location')} />
              <Tile label="No Revenue" value={integer(gc.revenue)} tone="amber" active={open === 'revenue'} onClick={() => toggle('revenue')} />
            </Box>
            {['unowned', 'nonSalesOwner', 'ownerUnclassified', 'industry', 'location', 'revenue'].map((g) => (
              <React.Fragment key={g}>{drill(g, <CompanyGapTable gap={g} hub={hub} />)}</React.Fragment>
            ))}

            <Panel title="Companies by HubSpot User" icon={<GroupsIcon sx={{ fontSize: 16 }} />} count={(c.byOwner || []).length}
              subtitle="Role from the owner's team (Data → Sales POCs). Support owners are reassign candidates; Unclassified owners need a team tag.">
              <OwnerTable rows={c.byOwner} />
            </Panel>

            <Panel title="Conglomerate Match" icon={<ViewInArIcon sx={{ fontSize: 16 }} />} count={c.conglomerateGroups}
              subtitle={`${integer(c.conglomerateCompanyCount)} companies across ${integer(c.conglomerateGroups)} conglomerates · click a row to see its companies`}>
              <ConglomerateTable rows={c.conglomerateMatch} hub={hub} />
            </Panel>
          </Box>

          {/* ── Open-deal hygiene ─────────────────────────────────────── */}
          <Box sx={{ mb: 3 }}>
            <SectionHeading icon={<WarningAmberIcon sx={{ fontSize: 18 }} />} color="#f59e0b" title="Open-Deal Hygiene"
              right={<Typography sx={{ fontSize: 12, color: GX_INK_400 }}>{integer(h.totalOpenDeals)} open deals · idle threshold {staleDays}d</Typography>} />
            <Box sx={grid(4)}>
              <Tile label={`Hot · idle > ${staleDays}d`} value={integer(h.hotIdle)} tone="rose" active={open === 'd_hot'} onClick={() => toggle('d_hot')} />
              <Tile label={`Warm · idle > ${staleDays}d`} value={integer(h.warmIdle)} tone="amber" active={open === 'd_warm'} onClick={() => toggle('d_warm')} />
              <Tile label="Amount ≠ Est MBR" value={integer(h.amountNeMbr)} sub="Amount set & differs (blank amounts excluded)" tone="amber" active={open === 'd_amt'} onClick={() => toggle('d_amt')} />
              <Tile label="Temperature blank" value={integer(h.temperatureBlank)} sub="Open, no temperature set (excl. Cold/Hibernating)" tone="rose" active={open === 'd_temp'} onClick={() => toggle('d_temp')} />
              <Tile label="Cold / Hibernating" value={integer(h.coldOrHibernating)} sub="Open deals parked cold — stale-pipeline signal" tone="amber" active={open === 'd_coldhib'} onClick={() => toggle('d_coldhib')} />
              <Tile label={`Mktg Hot · idle > ${staleDays}d`} value={integer(h.mktHotIdle)} tone="rose" active={open === 'd_mhot'} onClick={() => toggle('d_mhot')} />
              <Tile label={`Mktg Warm · idle > ${staleDays}d`} value={integer(h.mktWarmIdle)} tone="amber" active={open === 'd_mwarm'} onClick={() => toggle('d_mwarm')} />
              <Tile label="Expected close past today" value={integer(h.expectedClosePastToday)} sub="Hot + Warm" tone="rose" active={open === 'd_exp'} onClick={() => toggle('d_exp')} />
              <Tile label="Duplicate open deals" value={integer(h.duplicateDeals)} sub={`${integer(h.duplicateGroups)} groups — same company, same pipeline, same deal renamed`} tone="rose" active={open === 'd_dup'} onClick={() => toggle('d_dup')} />
            </Box>
            {drill('d_hot', <DealTable rows={D.hotStale} hub={hub} />)}
            {drill('d_warm', <DealTable rows={D.warmStale} hub={hub} />)}
            {drill('d_amt', <DealTable rows={D.amountMbrMismatch} hub={hub} />)}
            {drill('d_temp', <DealTable rows={D.temperatureBlank} hub={hub} />)}
            {drill('d_mhot', <DealTable rows={D.mktHotStale} hub={hub} />)}
            {drill('d_mwarm', <DealTable rows={D.mktWarmStale} hub={hub} />)}
            {drill('d_exp', <DealTable rows={D.expectedClosePast} hub={hub} />)}
            {drill('d_coldhib', <DealTable rows={D.coldHibernating} hub={hub} />)}
            {drill('d_dup', <DuplicateGroups groups={h.duplicateGroupRows} hub={hub} />)}
          </Box>

          {/* ── Sales-POC-wise gaps ───────────────────────────────────── */}
          <Panel title="Sales POC-wise Deal Gaps" icon={<GroupsIcon sx={{ fontSize: 16 }} />} count={(h.pocRows || []).length}
            subtitle="Open-deal hygiene issues attributed to each owner · sortable · filterable" defaultOpen>
            <PocGapTable rows={h.pocRows} deals={D} hub={hub} />
          </Panel>
        </>
      )}
    </Box>
  )
}
